import React, { useState, useEffect, useCallback } from 'react';
import { object, func } from 'prop-types';
import { connect } from 'react-redux';
import { Form } from 'antd';

import { closeCurrentModal } from '../modal-reducer';
import { ButtonWithLoader } from '../../components/ui-helpers';
import { handleRequestUpdate } from '../../redux/thunk/requests';
import { validateNumbersRange, validateNumberInput } from '../../utils';

import {
  Title,
  FormItemS,
  BudgetInput,
  RangeDivider,
  RangeLabelText,
  BudgetCheckbox,
  BudgetInputPrefix,
  RangeInputsWrapper,
  RangeButtonWrapper,
  RangeInputContainer,
  FormErrorWrapper,
  DeleteBudgetWrapper,
  BudgetActionWrapper,
} from './styled-components';
import { theme } from '../../styles';
import SvgIcon from '../../components/svg-icon';
import { Popconfirm } from '../../components/popovers';

export const contentRangeInput = (
  errorMessage,
  getFieldDecorator,
  defaultBudget,
  minBudgetHandler,
  maxBudgetHandler,
  isRange,
) => isRange ? (
  <FormErrorWrapper
    validateStatus={errorMessage ? 'error' : 'success'}
    help={errorMessage && errorMessage}
    message={errorMessage}
  >
    <RangeInputsWrapper >
      <FormItemS>
        {getFieldDecorator('min', {
          initialValue: defaultBudget[0],
        })(<BudgetInput
          onChange={minBudgetHandler}
          placeholder="1 000"
          size="small"
          name="min"
          type="number"
          addonBefore={<BudgetInputPrefix>$</BudgetInputPrefix>}
        />)}
      </FormItemS>
      <RangeDivider />
      <FormItemS >
        {getFieldDecorator('max', {
          initialValue: defaultBudget[1],
        })(<BudgetInput
          onChange={maxBudgetHandler}
          placeholder="10 000"
          size="small"
          name="max"
          type="number"
          addonBefore={<BudgetInputPrefix>$</BudgetInputPrefix>}
        />)}
      </FormItemS>
    </RangeInputsWrapper>
  </FormErrorWrapper>
) : (
  <FormErrorWrapper
    validateStatus={errorMessage ? 'error' : 'success'}
    help={errorMessage && errorMessage}
    message={errorMessage}
  >
    <FormItemS>
      {getFieldDecorator('min', {
        initialValue: defaultBudget[0],
      })(<BudgetInput
        onChange={minBudgetHandler}
        placeholder="10 000"
        size="small"
        name="min"
        type="number"
        addonBefore={<BudgetInputPrefix>$</BudgetInputPrefix>}
      />)}
    </FormItemS>
  </FormErrorWrapper>
);

export const budgetModalComponent = ({
  modal,
  requests,
  closeModal,
  updateRequestAction,
  form: {
    getFieldDecorator, getFieldsValue, setFields, validateFields,
  },
}) => {
  const [isRange, setIsRange] = useState(false);
  const [defaultBudget, setDefaultBudget] = useState([]);
  const [hasBudget, setHasBudget] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const minBudgetHandler = (event) => {
    setFields({
      min: {
        value: event.target.value,
      },
    });

    if (isRange) {
      validateFields(['max'], { force: true });

      const { min, max } = getFieldsValue(['min', 'max']);

      setErrorMessage(validateNumbersRange([min, max]));
    } else {
      const { min } = getFieldsValue(['min']);

      setErrorMessage(validateNumberInput(min));
    }
  };

  const maxBudgetHandler = (event) => {
    setFields({
      max: {
        value: event.target.value,
      },
    });
    validateFields(['min'], { force: true });

    const { min, max } = getFieldsValue(['min', 'max']);

    setErrorMessage(validateNumbersRange([min, max]));
  };

  const checkboxHandler = useCallback(() => {
    setIsRange(!isRange);

    if (!isRange) {
      const { min, max } = getFieldsValue(['min', 'max']);

      setErrorMessage(validateNumbersRange([min, max]));
    } else {
      const { min } = getFieldsValue(['min']);
      setDefaultBudget([min]);

      setErrorMessage(validateNumberInput(min));
    }
  }, [isRange, setIsRange, getFieldsValue]);

  useEffect(() => {
    const { budget } = requests.items[modal.payload.requestId];
    if (budget && budget.length > 0) {
      setHasBudget(true);
      setDefaultBudget(requests.items[modal.payload.requestId].budget);
    }
  }, []);

  useEffect(() => {
    if (defaultBudget.filter(x => x).length < 2) {
      setIsRange(false);
    } else {
      setIsRange(true);
    }
  }, [defaultBudget]);

  const submitHandler = (event) => {
    event.preventDefault();
    const { min, max } = getFieldsValue(['min', 'max']);

    if (isRange) {
      const error = validateNumbersRange([min, max]);
      setErrorMessage(error);

      if (!error) {
        closeModal();
        return updateRequestAction(modal.payload.requestId, {
          budget: [+min, +max],
        });
      }
    } else {
      const error = validateNumberInput(min);
      setErrorMessage(error);

      if (!error) {
        closeModal();
        return updateRequestAction(modal.payload.requestId, {
          budget: [+min],
        });
      }
    }
    return null;
  };

  const deleteBudgetHandler = () => {
    closeModal();
    return updateRequestAction(modal.payload.requestId, {
      budget: [],
    });
  };

  return (
    <RangeInputContainer>
      <Form onSubmit={submitHandler}>
        <Title>Budget</Title>
        <RangeLabelText>Budget</RangeLabelText>
        <div>{
          contentRangeInput(
            errorMessage,
            getFieldDecorator,
            defaultBudget,
            minBudgetHandler,
            maxBudgetHandler,
            isRange,
          )
        }
        </div>
        <BudgetActionWrapper>
          <div>
            <BudgetCheckbox checked={isRange} onClick={checkboxHandler}>
            Range
            </BudgetCheckbox>
          </div>
          {
            hasBudget &&
            <Popconfirm
              title="Are you sure to delete current budget?"
              onConfirm={() => deleteBudgetHandler()}
            >
              <DeleteBudgetWrapper>
                <SvgIcon hoverColor={theme.colors.red} type="trash" width={14} />
              Delete budget
              </DeleteBudgetWrapper>
            </Popconfirm>
          }
        </BudgetActionWrapper>
        <RangeButtonWrapper>
          <ButtonWithLoader height="38px" maxWidth={152} type="submit">
            Save
          </ButtonWithLoader>
        </RangeButtonWrapper>
      </Form>
    </RangeInputContainer>
  );
};

budgetModalComponent.propTypes = {
  updateRequestAction: func.isRequired,
  modal: object.isRequired,
  form: object.isRequired,
  closeModal: func.isRequired,
  requests: object.isRequired,
};

const mapDispatchToProps = dispatch => ({
  updateRequestAction: (requestId, modifier) => dispatch(handleRequestUpdate(requestId, modifier)),
  closeModal: () => dispatch(closeCurrentModal()),
});

const mapStateToProps = (state) => {
  const { modal, requests } = state;
  return { modal, requests };
};

const component = connect(
  mapStateToProps,
  mapDispatchToProps,
)(budgetModalComponent);

export const BudgetModal = Form.create({ name: 'modal_form' })(component);



// WEBPACK FOOTER //
// ./src/modals/budget-modal/index.js